// file: src/components/Buildups.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Minus, Plus, GripVertical, HelpCircle, X, ArrowUpDown, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import MaterialDatabaseExplorer from './MaterialDatabaseExplorer';
import Step3Calculations, { ShortlistTable, WallAssignmentsTable, DEFAULT_SELECTED, buildWallRows } from './Step3Calculations';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { useWallBuildups } from './lca/LibraryPanel';

function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}
function defaultColor(i) {
  const PALETTE = ['#FBCFE8','#D1FAE5','#BFDBFE','#FDE68A','#C7D2FE','#FCA5A5','#A7F3D0','#E9D5FF','#FCD34D','#BFEDE3','#F9A8D4','#E5E7EB'];
  return PALETTE[i % PALETTE.length];
}
function formatThickness(cm) {
  if (!Number.isFinite(cm)) return '-';
  if (cm < 10) return `${cm.toFixed(1)} cm`;
  return `${Math.round(cm)} cm`;
}
function clamp(min, v, max) {
  return Math.max(min, Math.min(v, max));
}

const PREVIEW_BAR_HEIGHT = 160;
const REFERENCE_CM = 40;

const cloneWall = (wall) => ({
  ...wall,
  layers: wall.layers.map((l) => ({ ...l })),
});

// One source of truth for column tracks (thinner thickness, wider name, tighter actions)
const COLS = '44px minmax(0,2.5fr) 64px 128px 60px';
const OPEN_STEPS_STORAGE_KEY = 'buildups-open-steps';
const BUILDUPS_STORAGE_KEY = 'embodix_buildups_state_v1';

const safeJsonParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    console.error('Failed to parse buildups storage', err);
    return null;
  }
};

const HorizontalWallPreview = ({ layers }) => {
  const positive = layers
    .map((l, i) => ({ ...l, color: l.color || defaultColor(i) }))
    .filter((l) => Number.isFinite(l.thicknessCm) && l.thicknessCm > 0);

  const total = positive.reduce((acc, l) => acc + l.thicknessCm, 0);
  const segments = useMemo(() => {
    if (total <= 0) return [];
    const raw = positive.map((l) => ({ id: l.id, w: (l.thicknessCm / total) * 100 }));
    const MIN = 3;
    const withMin = raw.map((r) => ({ ...r, w: Math.max(MIN, r.w) }));
    if (withMin.length) {
      const totalWidth = withMin.reduce((a, r) => a + r.w, 0);
      const factor = 100 / totalWidth;
      withMin.forEach((r) => (r.w = r.w * factor));
    }
    return positive.map((l, idx) => ({ ...l, widthPct: withMin[idx].w }));
  }, [positive, total]);

  // Responsive bar width centered in the track
  const scale = clamp(0.35, total / REFERENCE_CM, 1);
  const barWidthPct = Math.round(scale * 100);

  return (
    <div className="h-full">
      <div className="pb-2 text-center">
        <h4 className="text-gray-800 font-semibold">Wall section preview</h4>
        <p className="text-sm text-black">
          Total thickness: <span className="font-medium text-black">{Math.round(total)} cm</span>
        </p>
      </div>

      {total <= 0 ? (
        <div className="text-sm text-gray-600 bg-emerald-50/70 border border-dashed border-emerald-200 rounded-xl p-4 text-center">
          Add layer thicknesses &gt; 0 cm to see a scaled horizontal section.
        </div>
      ) : (
        <div className="space-y-2">
          {/* Label row ABOVE the diagram that tracks the same width */}
          <div className="w-full flex justify-center">
            <div
              className="flex justify-between text-[11px] text-gray-700 select-none w-full max-w-full"
              style={{ width: `${barWidthPct}%` }}
              aria-hidden="true"
            >
              <span>Outside</span>
              <span>Inside</span>
            </div>
          </div>

          {/* Track with a centered inner bar; no bevel/ring/shadow */}
          <div className="relative w-full bg-white overflow-hidden" style={{ height: PREVIEW_BAR_HEIGHT }}>
            <div className="h-full w-full flex items-stretch justify-center">
              <div className="h-full relative flex" style={{ width: `${barWidthPct}%` }}>
                {segments.map((l, idx) => (
                  <div
                    key={l.id}
                    className="relative h-full border-r border-white/60 last:border-r-0"
                    style={{ width: `${l.widthPct}%`, background: l.color }}
                    title={`${l.name || 'Layer'} — ${formatThickness(l.thicknessCm)}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/85 text-[10px] font-medium text-gray-800 shadow-sm">
                        {idx + 1}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// One wall section editor (card without drop shadow)
const WallSectionEditor = ({ wall, onChange, onRemove, canRemove, onSave }) => {
  const layers = wall.layers;
  const [savedOnce, setSavedOnce] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState('');
  const setLayers = (next) => {
    const resolved = typeof next === 'function' ? next(wall.layers) : next;
    onChange({ ...wall, layers: resolved });
  };

  const totalThickness = useMemo(
    () => layers.reduce((acc, l) => acc + (Number.isFinite(l.thicknessCm) ? Math.max(0, l.thicknessCm) : 0), 0),
    [layers]
  );

  const addLayerBelow = (index) => {
    const next = [...layers];
    next.splice(index + 1, 0, {
      id: uid(),
      name: `Layer ${layers.length + 1}`,
      thicknessCm: 5,
      color: defaultColor(layers.length),
    });
    setLayers(next);
  };
  const removeLayer = (id) => {
    if (layers.length <= 1) return;
    setLayers(layers.filter((l) => l.id !== id));
  };
  const updateLayer = (id, patch) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };
  const reverseLayers = () => setLayers((prev) => [...prev].reverse());

  const dragIndexRef = useRef(null);
  const handleDragStart = (index) => (e) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };
  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (index) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from == null || from === index) return;
    const next = [...layers];
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    setLayers(next);
  };

  return (
    <div className="space-y-1 pt-0 pb-1">
      {/* Top: name + (optional) remove + instructions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-emerald-700 leading-none whitespace-nowrap">Section name</span>
          <Input
            className="h-7 w-32 sm:w-56 text-gray-900 placeholder:text-gray-400"
            value={wall.name}
            onChange={(e) => onChange({ ...wall, name: e.target.value })}
            placeholder="e.g., Wall A"
            aria-label="Wall section name"
          />

          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 p-0 text-red-600"
              onClick={onRemove}
              title="Remove this wall section"
              aria-label="Remove wall section"
            >
              <X className="w-4 h-4" />
            </Button>
          )}

          <Dialog>
            <DialogTrigger asChild>
              <Button type="button" variant="ghost" className="h-7 w-7 p-0 text-emerald-700 sm:h-8 sm:w-auto sm:px-3">
                <HelpCircle className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Instructions</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="w-[92vw] max-w-md sm:w-full">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-center mb-2 -mt-1">How to use wall sections</DialogTitle>
              <DialogDescription>
                Build each wall per m². Name each section to compare options in Step 3.
                Design the wall <span className="font-medium text-emerald-800">Outside → Inside</span> and set thickness in centimeters.
                Drag the <span className="font-medium text-emerald-800">grip handle</span> to reorder layers.
              </DialogDescription>
            </DialogHeader>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-2">
              <li>Add or remove layers with the + / − buttons.</li>
              <li>Use “Reverse order” (↕) to flip Outside/Inside.</li>
              <li>Drag the grip handle to reorder layers.</li>
            </ul>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      <div className="relative">
        <div className="grid gap-6 lg:gap-10 lg:grid-cols-2 items-stretch">
          {/* LEFT: fixed-height, center-aligned preview (no shadow) */}
          <div className="min-w-0 h-[280px] rounded-lg border border-emerald-200 bg-white p-3">
            <HorizontalWallPreview layers={layers} />
          </div>

          {/* RIGHT: fixed-height inputs; inner list scrolls */}
          <div className="min-w-0 h-[280px] rounded-lg border border-emerald-200 bg-white p-3 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <div className="h-7 text-xs text-black bg-emerald-50 border border-emerald-100 rounded px-2 flex items-center">
                Total thickness: <span className="font-medium text-black">{Math.round(totalThickness)} cm</span>
              </div>
              <div className="flex items-center gap-2">
                {savingFeedback && <span className="text-[11px] text-emerald-700">{savingFeedback}</span>}
                <Button
                  type="button"
                  className="h-7 px-3 bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() => {
                    setSavedOnce(true);
                    setSavingFeedback(savedOnce ? 'Updated!' : 'Saved!');
                    onSave?.();
                    setTimeout(() => setSavingFeedback(''), 1200);
                  }}
                  title={savedOnce ? 'Update the saved layer information' : 'Save the current layer information'}
                >
                  {savedOnce ? 'Update info' : 'Save info'}
                </Button>
              </div>
            </div>

            <div className="relative flex-1 min-h-0">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-emerald-700 lg:hidden"
                onClick={reverseLayers}
                title="Reverse Outside/Inside order"
                aria-label="Reverse Outside/Inside order"
              >
                <ArrowUpDown className="w-4 h-4" />
              </Button>

              <div className="ml-8 lg:ml-0 h-full overflow-x-auto rounded-md border border-emerald-100 bg-white/90 flex flex-col min-h-0">
                <div className="min-w-[520px] flex flex-col min-h-0">
                  {/* Header row — EXACT same columns as body rows */}
                  <div
                    className="grid items-center px-2 py-1 bg-emerald-50 text-emerald-800 text-[11px] font-semibold text-center"
                    style={{ gridTemplateColumns: COLS }}
                  >
                    <span>#</span>
                    <span>Layer name</span>
                    <span>Thick (cm)</span>
                    <span>Color (HEX)</span>
                    <span>Actions</span>
                  </div>

                  <ScrollArea className="flex-1 min-h-0">
                    <div className="divide-y divide-emerald-100">
                      {layers.map((l, i) => (
                        <div
                          key={l.id}
                          className="grid items-center px-2 py-1 text-sm"
                          style={{ gridTemplateColumns: COLS }}
                          onDragOver={handleDragOver(i)}
                          onDrop={handleDrop(i)}
                        >
                          <div
                            className="text-emerald-700 text-xs select-none cursor-grab flex items-center gap-1"
                            title="Drag to reorder"
                            draggable
                            onDragStart={handleDragStart(i)}
                            onDragEnd={() => { dragIndexRef.current = null; }}
                          >
                            <GripVertical className="w-3.5 h-3.5" />
                            <span className="inline-block w-4 text-center">{i + 1}</span>
                          </div>

                        <div className="pr-1">
                          <Input
                            className="h-7 text-sm w-full text-gray-900 placeholder:text-gray-400"
                            value={l.name}
                            onChange={(e) => updateLayer(l.id, { name: e.target.value })}
                            placeholder="e.g., Insulation, Concrete"
                            aria-label={`Layer ${i + 1} name`}
                          />
                        </div>

                        <div className="pr-1">
                          <Input
                            className="h-7 text-sm px-1.5 text-gray-900 placeholder:text-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            type="number"
                            step="0.1"
                            min="0"
                            value={Number.isFinite(l.thicknessCm) ? l.thicknessCm : ''}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              updateLayer(l.id, { thicknessCm: Number.isFinite(v) ? Math.max(0, v) : 0 });
                            }}
                            aria-label={`Layer ${i + 1} thickness in cm`}
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-4 w-4 rounded border"
                            style={{ background: l.color || defaultColor(i), borderColor: '#cfe9dc' }}
                            aria-label={`Layer ${i + 1} color preview`}
                          />
                          <Input
                            className="h-7 w-[96px] text-xs font-mono text-gray-900 placeholder:text-gray-400"
                            value={(l.color || defaultColor(i)).toUpperCase()}
                            onChange={(e) => {
                              const value = e.target.value.trim();
                              if (/^#?[0-9A-Fa-f]{0,6}$/.test(value)) {
                                updateLayer(l.id, { color: value.startsWith('#') ? value : `#${value}` });
                              }
                            }}
                            onBlur={(e) => {
                              let value = e.target.value.trim();
                              if (!value) { updateLayer(l.id, { color: defaultColor(i) }); return; }
                              if (!value.startsWith('#')) value = `#${value}`;
                              if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                updateLayer(l.id, { color: value.toUpperCase() });
                              } else {
                                updateLayer(l.id, { color: defaultColor(i) });
                              }
                            }}
                            aria-label={`Layer ${i + 1} color hex code`}
                            maxLength={7}
                          />
                        </div>

                        <div className="flex items-center justify-start gap-1">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-600"
                            onClick={() => removeLayer(l.id)}
                            aria-label={`Remove layer ${i + 1}`}
                            disabled={layers.length <= 1}
                            title="Remove layer"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-emerald-700"
                            onClick={() => addLayerBelow(i)}
                            aria-label={`Add a layer after ${i + 1}`}
                            title="Add layer below"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 p-0 text-emerald-700 bg-white border border-emerald-100 shadow-sm"
            onClick={reverseLayers}
            title="Reverse Outside/Inside order"
            aria-label="Reverse Outside/Inside order"
          >
            <ArrowUpDown className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const Buildups = () => {
  const [openSteps, setOpenSteps] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const raw = window.sessionStorage.getItem(OPEN_STEPS_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
        }
      } catch (_) {
        /* ignore */
      }
    }
    return [];
  });

  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (Array.isArray(openSteps) && openSteps.length > 0) {
        window.sessionStorage.setItem(OPEN_STEPS_STORAGE_KEY, JSON.stringify(openSteps));
      } else {
        window.sessionStorage.removeItem(OPEN_STEPS_STORAGE_KEY);
      }
    } catch (_) {
      /* ignore */
    }
  }, [openSteps]);
  const makeDefaultWall = (n) => ({
    id: uid(),
    name: `Wall ${String.fromCharCode(65 + n)}`, // Wall A, B, C...
    layers: [
      { id: uid(), name: 'Exterior finish', thicknessCm: 2, color: '#E5E7EB' },
      { id: uid(), name: 'Insulation', thicknessCm: 8, color: '#D1FAE5' },
      { id: uid(), name: 'Concrete / masonry', thicknessCm: 18, color: '#BFDBFE' },
      { id: uid(), name: 'Interior finish', thicknessCm: 1.6, color: '#FBCFE8' },
    ],
  });

  const [walls, setWalls] = useState(() => [makeDefaultWall(0)]);
  const [shortlisted, setShortlisted] = useState([]);
  const [editCustomEpd, setEditCustomEpd] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [adjustments, setAdjustments] = useState({});
  const [selectedStages, setSelectedStages] = useState(() => new Set(DEFAULT_SELECTED));
  const [collapsedWalls, setCollapsedWalls] = useState({});
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [pendingRemoveWallId, setPendingRemoveWallId] = useState(null);
  const { upsertSavedWall, removeSavedWall } = useWallBuildups();
  const getSavedWallId = (wall) => wall?.savedId || wall?.id || null;
  const shortlistByUuid = useMemo(() => {
    const map = new Map();
    (shortlisted || []).forEach((d) => map.set(d.uuid, d));
    return map;
  }, [shortlisted]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(BUILDUPS_STORAGE_KEY);
    const stored = safeJsonParse(raw);
    if (stored) {
      if (Array.isArray(stored.walls) && stored.walls.length > 0) {
        setWalls(stored.walls);
      }
      if (Array.isArray(stored.shortlisted)) {
        setShortlisted(stored.shortlisted);
      }
      if (stored.assignments && typeof stored.assignments === 'object') {
        setAssignments(stored.assignments);
      }
      if (stored.adjustments && typeof stored.adjustments === 'object') {
        setAdjustments(stored.adjustments);
      }
      if (Array.isArray(stored.selectedStages) && stored.selectedStages.length > 0) {
        setSelectedStages(new Set(stored.selectedStages));
      }
      if (stored.collapsedWalls && typeof stored.collapsedWalls === 'object') {
        setCollapsedWalls(stored.collapsedWalls);
      }
      if (Array.isArray(stored.openSteps) && stored.openSteps.length > 0) {
        setOpenSteps(stored.openSteps);
      }
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated || typeof window === 'undefined') return;
    const payload = {
      walls,
      shortlisted,
      assignments,
      adjustments,
      selectedStages: Array.from(selectedStages || []),
      collapsedWalls,
      openSteps
    };
    try {
      window.localStorage.setItem(BUILDUPS_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      console.error('Failed to persist buildups state', err);
    }
  }, [
    walls,
    shortlisted,
    assignments,
    adjustments,
    selectedStages,
    collapsedWalls,
    openSteps,
    hydrated
  ]);

  useEffect(() => {
    if (walls.length <= 3) {
      setCollapsedWalls({});
    }
  }, [walls.length]);

  const upsertShortlist = (payload) => {
    if (!payload || !payload.uuid) return;
    if (payload.__remove) {
      setShortlisted((prev) => prev.filter((x) => x.uuid !== payload.uuid));
      return;
    }
    setShortlisted((prev) => {
      if (payload.__update) {
        return prev.map((x) => (x.uuid === payload.uuid ? { ...x, ...payload } : x));
      }
      if (prev.some((x) => x.uuid === payload.uuid)) return prev;
      const next = {
        id: payload.uuid,
        uuid: payload.uuid,
        name: payload.name,
        declaredUnit: payload.declaredUnit || null,
        gwpTotal: Number(payload.gwpTotal) || 0,
        modules: payload.modules || null,
        materialProperties: payload.materialProperties || null,
        geography: payload.geography || null,
        subtype: payload.subtype || null,
        manufacturer: payload.manufacturer || null,
        source: payload.source || null,
        addedAt: Date.now(),
      };
      return [...prev, next];
    });
  };
  const toggleWallCollapsed = (id) => {
    setCollapsedWalls((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const removeFromShortlist = (uuid) => {
    setShortlisted((prev) => prev.filter((x) => x.uuid !== uuid));
  };

  const addWall = () => {
    setWalls((prev) => [...prev, makeDefaultWall(prev.length)]);
  };
  const updateWall = (id, patch) => {
    setWalls((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  };
  const removeWall = (id) => {
    let savedId = null;
    setWalls((prev) => {
      const target = prev.find((w) => w.id === id);
      savedId = getSavedWallId(target);
      return prev.length > 1 ? prev.filter((w) => w.id !== id) : prev;
    });
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setAdjustments((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (savedId) removeSavedWall(savedId);
  };
  const requestRemoveWall = (id) => {
    setPendingRemoveWallId(id);
    setRemoveDialogOpen(true);
  };
  const setAssignment = (wallId, layerId, uuid) => {
    setAssignments((prev) => ({
      ...prev,
      [wallId]: { ...(prev[wallId] || {}), [layerId]: uuid || '' },
    }));
  };
  const setAdjustment = (wallId, layerId, value) => {
    const clean = Number.isFinite(value) ? value : 1;
    setAdjustments((prev) => ({
      ...prev,
      [wallId]: { ...(prev[wallId] || {}), [layerId]: clean },
    }));
  };
  const saveWall = (wall) => {
    const savedId = getSavedWallId(wall);
    const { total, hasCalculatedLayer } = buildWallRows({
      wall,
      assignments,
      shortlistByUuid,
      selectedStages,
      adjustments,
    });
    const payload = {
      id: savedId,
      name: wall.name || '',
      layers: wall.layers.map((layer) => ({ ...layer })),
      totalGwpPerM2: hasCalculatedLayer ? Number(total) : null,
      selectedStages: Array.from(selectedStages || []),
      savedAt: Date.now(),
    };
    upsertSavedWall(payload);
    if (!wall.savedId && savedId) {
      updateWall(wall.id, { savedId });
    }
  };

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="text-center space-y-2">
        <h2 className="text-[1.25rem] font-semibold text-gray-800 leading-tight">Evaluate the impact of your system</h2>
        <p className="text-sm text-gray-600">
          Define and visualize an element&apos;s buildups and layers. Browse environmental datasets to iterate and compare the impacts.
        </p>
      </div>

      <Accordion
        type="multiple"
        value={openSteps}
        onValueChange={(value) => setOpenSteps(Array.isArray(value) ? value : [])}
        className="space-y-3"
      >
        <AccordionItem value="step1" className="border-none">
          <div className="rounded-xl border border-[#245040] bg-white">
            <AccordionTrigger className="w-full px-5 py-4 text-left text-gray-800 hover:no-underline">
              <div className="flex w-full flex-col gap-0.5 text-left select-text">
                <span className="text-base font-semibold text-gray-800">Step 1 - Find or create your own materials</span>
                <p className="text-sm text-muted-foreground font-normal">
                  Use the explorer below to search materials and add them to your buildup definitions.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 pt-0">
              <MaterialDatabaseExplorer
                onShortlist={upsertShortlist}
                shortlist={shortlisted}
                editEpd={editCustomEpd}
                onEditComplete={() => setEditCustomEpd(null)}
              />
              <div className="mt-4">
                <ShortlistTable
                  shortlistedDatasets={shortlisted}
                  onRemoveShortlist={removeFromShortlist}
                  onEditShortlist={(item) => setEditCustomEpd(item)}
                />
              </div>
            </AccordionContent>
          </div>
        </AccordionItem>

        <AccordionItem value="step2" className="border-none">
          <div className="rounded-xl border border-[#245040] bg-white">
            <AccordionTrigger className="w-full px-5 py-4 text-left text-gray-800 hover:no-underline">
              <div className="flex w-full flex-col gap-0.5 text-left select-text">
                <span className="text-base font-semibold text-gray-800">Step 2 - Design wall sections</span>
                <p className="text-sm text-muted-foreground font-normal">
                  Build your wall sections (each models one m2). Name sections to keep them distinct. These will be used to map layers against shortlisted datasets and compare results.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 pt-0">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">Add alternatives to compare later.</div>
                  <Button
                    type="button"
                    variant="secondary"
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    onClick={addWall}
                    title="Add a new wall section"
                  >
                    + Wall section
                  </Button>
                </div>

                <div className="rounded-xl border border-emerald-100 bg-white divide-y divide-emerald-100">
                  {walls.map((wall, idx) => {
                    const allowCollapse = walls.length > 3;
                    const isCollapsed = allowCollapse && collapsedWalls[wall.id];
                    return (
                      <div key={wall.id} className={isCollapsed ? 'py-2 px-3' : 'py-4 px-3'}>
                        {allowCollapse && (
                          <div className="flex items-center gap-2 mb-2">
                            <div className="text-sm font-semibold text-emerald-900 flex-1 text-center sm:text-left">
                              {wall.name || `Wall ${idx + 1}`}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => toggleWallCollapsed(wall.id)}
                              aria-label={isCollapsed ? 'Expand wall section' : 'Collapse wall section'}
                            >
                              <ChevronDown className={`h-4 w-4 transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-0'}`} />
                            </Button>
                          </div>
                        )}

                        <div className={allowCollapse && isCollapsed ? 'hidden' : 'space-y-2'}>
                          <WallSectionEditor
                            key={`${wall.id}-editor`}
                            wall={wall}
                            onChange={(next) => updateWall(wall.id, next)}
                            onRemove={() => requestRemoveWall(wall.id)}
                            canRemove={walls.length > 1}
                            onSave={() => saveWall(wall)}
                          />

                          <WallAssignmentsTable
                            wall={wall}
                            shortlistedDatasets={shortlisted}
                            assignments={assignments}
                            onAssign={setAssignment}
                            adjustments={adjustments}
                            onAdjustmentChange={setAdjustment}
                            selectedStages={selectedStages}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </AccordionContent>
          </div>
        </AccordionItem>



        <AccordionItem value="step3" className="border-none">
          <div className="rounded-xl border border-[#245040] bg-white">
            <AccordionTrigger className="w-full px-5 py-4 text-left text-gray-800 hover:no-underline">
              <div className="flex w-full flex-col gap-0.5 text-left select-text">
                <span className="text-base font-semibold text-gray-800">Step 3 - Results</span>
                <p className="text-sm text-muted-foreground font-normal">
                  View your results and compare lifecycle totals across wall sections.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 pt-0">
                <Step3Calculations
                  shortlistedDatasets={shortlisted}
                  walls={walls}
                  assignments={assignments}
                  adjustments={adjustments}
                  selectedStages={selectedStages}
                  onSelectedStagesChange={setSelectedStages}
                  hideHeader
                  unstyled
                />
            </AccordionContent>
          </div>
        </AccordionItem>
      </Accordion>

      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Remove wall buildup?</DialogTitle>
            <DialogDescription className="text-gray-700">
              This will permanently remove the wall buildup and cannot be reverted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="text-gray-900"
              onClick={() => setRemoveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                if (pendingRemoveWallId) removeWall(pendingRemoveWallId);
                setPendingRemoveWallId(null);
                setRemoveDialogOpen(false);
              }}
            >
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Buildups;
